import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import type { OperationProgressModel, OperationProgressStep } from '../../../shared/ui/progress';
import type { ExportFlowStep } from '../../../shared/api/grpc/pcfExportClient';
import type { UseExportStateReturn } from '../hooks/useExportState';

interface ExportProgressRequestProps {
  open: boolean;
  pickBusy: boolean;
  validating: boolean;
  pickStep: ExportFlowStep | null;
  pickProgress: OperationProgressModel | null;
  selectedCount: number;
  exportState: UseExportStateReturn;
}

export default function ExportProgressRequest(props: ExportProgressRequestProps) {
  // The window also opens while the pre-export validation preflight runs,
  // before either export flow has started — rendering the regular fallback
  // there would falsely claim "Revit is exporting N piping systems".
  const validatingOnly =
    props.validating && !props.pickBusy && props.exportState.exportStep === null;
  if (validatingOnly) {
    return (
      <OperationProgressWindowRequest
        id="pcf-export-progress"
        open={props.open}
        priority={80}
        model={{
          title: 'Validating before export',
          message: 'Running pre-export validation — the export starts when validation finishes.',
          detail: props.exportState.config.outputPath
            ? `Output folder: ${props.exportState.config.outputPath}`
            : undefined,
          status: 'running',
          percent: null,
          locked: true,
          steps: [{ id: 'validate', label: 'Running pre-export validation', status: 'current' }],
        }}
      />
    );
  }
  const currentStep = props.pickBusy ? props.pickStep : props.exportState.exportStep;
  const stepOrder: ExportFlowStep[] = ['license', 'calculate', 'write', 'record'];
  const currentStepIndex = currentStep ? stepOrder.indexOf(currentStep) : -1;
  const stepStatus = (step: ExportFlowStep): OperationProgressStep['status'] => {
    const index = stepOrder.indexOf(step);
    return index === currentStepIndex ? 'current' : currentStepIndex > index ? 'complete' : 'pending';
  };
  const steps: OperationProgressStep[] = [
    { id: 'license', label: '① Checking license and quota', status: stepStatus('license') },
    {
      id: 'calculate',
      label: props.pickBusy
        ? '② Pick elements and calculate in the plugin'
        : '② Calculate PCF files in the plugin',
      status: stepStatus('calculate'),
    },
    { id: 'write', label: '③ Writing and verifying files', status: stepStatus('write') },
    { id: 'record', label: '④ Recording confirmed export files', status: stepStatus('record') },
  ];
  const fallback: OperationProgressModel = {
    title: props.pickBusy ? 'Exporting picked Revit elements' : 'Exporting PCF files',
    message: messageForStep(currentStep, props.pickBusy, props.selectedCount, props.exportState.systems.length),
    detail: props.exportState.config.outputPath
      ? `Output folder: ${props.exportState.config.outputPath}`
      : 'Waiting for the export output folder',
    status: 'running',
    percent: null,
    locked: true,
    metrics: props.pickBusy
      ? [{ label: 'Mode', value: 'Picked elements' }]
      : [
          { label: 'Selected', value: String(props.selectedCount) },
          { label: 'Available systems', value: String(props.exportState.systems.length) },
        ],
  };
  const base = props.pickBusy && props.pickProgress
    ? props.pickProgress
    : !props.pickBusy && props.exportState.exportProgress
      ? props.exportState.exportProgress
      : fallback;
  const trace = props.exportState.exportTraceId
    ? [{ label: 'Trace ID', value: props.exportState.exportTraceId }]
    : [];

  return (
    <OperationProgressWindowRequest
      id="pcf-export-progress"
      open={props.open}
      priority={80}
      model={{ ...base, steps, metrics: [...(base.metrics ?? []), ...trace] }}
    />
  );
}

function messageForStep(
  step: ExportFlowStep | null,
  pickBusy: boolean,
  selectedCount: number,
  systemCount: number
) {
  switch (step) {
    case 'license': return '① Checking license and PCF export quota…';
    case 'calculate': return pickBusy
      ? '② Pick piping elements in Revit for local calculation…'
      : '② Calculating PCF files in the PipeEX plugin…';
    case 'write': return '③ Writing and verifying PCF files…';
    case 'record': return '④ Recording confirmed PCF files…';
    default: return selectedCount > 0
      ? `Revit is exporting ${selectedCount} selected piping system${selectedCount === 1 ? '' : 's'}`
      : `Revit is exporting ${systemCount} piping system${systemCount === 1 ? '' : 's'}`;
  }
}
